import React, { useEffect } from 'react';

import { Box, Button, Grid, Typography } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Tooltip } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormSelect,
    ReactHookFormToggleButtonGroup,
} from 'common/components/form/bootstrap';
import ShowErrors from 'common/components/ShowErrors';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import { getPanelBrandsOptions, getPanelsOptions } from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

const ProposalForm = ({
    errors,
    fetchPanels,
    initialValues,
    isSaving,
    panelBrands,
    panels,
    saveProposal,
}) => {
    const { t } = useTranslation();
    const { control, handleSubmit, reset, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    const panelBrand = watch('panel_brand');

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        if (!isEmpty(panelBrand)) fetchPanels(panelBrand);
    }, [panelBrand]);

    const handleClickSave = (values) => saveProposal(values);

    return (
        <form>
            <Grid container>
                <Grid size="grow">
                    <ReactHookFormInput
                        control={control}
                        label={t('Proposal name')}
                        name="name"
                    />

                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        label={t('Panel brand')}
                        name="panel_brand"
                        options={getPanelBrandsOptions(panelBrands)}
                    />

                    <ReactHookFormSelect
                        autoComplete="off"
                        control={control}
                        disabled={isEmpty(panelBrand)}
                        label={t('Panel')}
                        name="panel"
                        options={getPanelsOptions(panels)}
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <ReactHookFormToggleButtonGroup
                            control={control}
                            fullWidth
                            name="type"
                            onChange={(_, value) => {
                                if (value === null) return;
                            }}
                            options={[
                                { label: t('Professional'), value: 'pro' },
                                { label: t('Express'), value: 'express' },
                            ]}
                        />

                        <Box>
                            <Tooltip
                                placement="top-start"
                                sx={{ maxWidth: '300px' }}
                            >
                                <Typography variant="body2" sx={{ p: 1 }}>
                                    {t(
                                        'Create proposals effortlessly, from quick express designs to detailed professional map layouts',
                                    )}
                                    :
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Box>

                    <ShowErrors errors={errors || []} />

                    <Button
                        disabled={isSaving}
                        fullWidth
                        onClick={handleSubmit(handleClickSave)}
                        sx={{ textTransform: 'initial' }}
                        variant="outlined"
                    >
                        {isSaving ? t('Saving').concat('...') : t('Continue')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

ProposalForm.propTypes = {
    errors: PropTypes.array,
    fetchPanels: PropTypes.func,
    initialValues: PropTypes.object,
    isSaving: PropTypes.bool,
    panelBrands: PropTypes.array,
    panels: PropTypes.array,
    saveProposal: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getSaveProposalErrors,
    initialValues: selectors.getInitialValues,
    isSaving: selectors.getIsSavingProposal,
});

const mapDispatchToProps = (dispatch) => ({
    saveProposal: (values) => dispatch(actions.saveProposal(values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProposalForm);
